/**
 * travelDatevMapper — reiner Adapter von einer abgerechneten Reise (`travel_trips`-Doc) auf die
 * entkoppelte DATEV-Eingabeform `DatevTravelInput` (RK-6, architecture-reisekosten.md §3.5/§3.6).
 *
 * `mapSettledTripToDatevTravelInput` ist eine reine, deterministische, seiteneffektfreie Funktion
 * (Muster `mapInvoiceDocToDatevInput`/`mapExpenseToDatevInput` in `datev/datevSource.ts`) — KEINE
 * Firebase-/React-Imports, voll unit-testbar. Sie liest die abgerechneten Pauschalen-Beträge aus
 * dem GoBD-Settlement-Snapshot (`settlement.calc`, SoT der abgerechneten Beträge — wird beim Lesen
 * NIE neu berechnet, §11 DB-Mapping) und leitet Belegfeld + Buchungstext deterministisch ab.
 *
 * Sinnvoll nur für Reisen MIT Settlement-Snapshot (settled/cancelled). Für einen `draft`-Trip ohne
 * Snapshot liefert die Funktion defensiv 0-Beträge; das Lebenszyklus-Gate greift dann in
 * `processTravelAllowance` (draft → Bucket).
 *
 * @module utils/travel/travelDatevMapper
 */

import type { DatevTravelInput } from '../datevExport';
import type { TravelTrip } from '../../types/travelTypes';

/**
 * Formatiert 'YYYY-MM-DD' als 'TT.MM.' (Tag.Monat., mit Schluss-Punkt) für den Buchungstext.
 *
 * @example toDdMm('2026-03-12') // '12.03.'
 */
function toDdMm(dateISO: string): string {
  const d = (dateISO ?? '').slice(0, 10);
  const dd = d.slice(8, 10);
  const mm = d.slice(5, 7);
  return `${dd}.${mm}.`;
}

/**
 * Baut das deterministische Beleg-Label (E5, §11 verbindliches Soll-Tripel):
 * `'RK-' + YYYYMMDD(returnDateISO) + '-' + docId.slice(0,6)` — Groß-/Kleinschreibung der Doc-ID
 * UNVERÄNDERT, Gesamtlänge exakt 18 Zeichen. Identische Formel wie `travelApi.settleTravelTrip`
 * (→ das hier abgeleitete Label ist byte-identisch zum persistierten `settlement.belegLabel`).
 *
 * @example buildTravelBelegLabel('2026-03-14', 'aB3xYz9QmT4kLp0Wc2Ef') // 'RK-20260314-aB3xYz'
 */
export function buildTravelBelegLabel(returnDateISO: string, tripId: string): string {
  const yyyymmdd = (returnDateISO ?? '').slice(0, 10).replace(/-/g, '');
  return `RK-${yyyymmdd}-${(tripId ?? '').slice(0, 6)}`;
}

/**
 * Baut die Buchungstext-Basis einer Reise, z. B. `'Reise Berlin 12.03.-14.03.'` (§11 Soll).
 * Der Export hängt später LEERZEICHEN-getrennt `'Verpflegung'`/`'Fahrtkosten'` an.
 */
export function buildTravelBuchungstextBase(t: TravelTrip): string {
  return `Reise ${t.destinationCity} ${toDdMm(t.departureDateISO)}-${toDdMm(t.returnDateISO)}`;
}

/**
 * Mappt eine abgerechnete Reise auf die entkoppelte `DatevTravelInput`-Form (RK-6).
 *
 * Beträge stammen 1:1 aus dem Settlement-Snapshot (`settlement.calc`, Integer-Cents) — SoT der
 * abgerechneten Beträge, NIE neu gerechnet. Fehlt der Snapshot (defensiv, z. B. draft), sind die
 * Beträge 0; das Lebenszyklus-Gate in `processTravelAllowance` bucketet solche Reisen ohnehin.
 *
 * @param t Reise-Dokument (`travel_trips`).
 * @returns Entkoppelte, belegloser Pauschalen-Eingabewert für `buildDatevExport`.
 */
export function mapSettledTripToDatevTravelInput(t: TravelTrip): DatevTravelInput {
  const calc = t.settlement?.calc ?? null;
  return {
    tripId: t.id,
    belegLabel: buildTravelBelegLabel(t.returnDateISO, t.id),
    buchungstextBase: buildTravelBuchungstextBase(t),
    returnDateISO: t.returnDateISO,
    verpflegungCents: calc?.verpflegungCents ?? 0,
    mileageCents: calc?.mileageCents ?? 0,
    status: t.status,
  };
}
